<script lang="ts">
  // Texture Pack editor. Pick the pack + the component, then edit its CSS. No bundled AI — instead
  // "Copy prompt for AI" exports a ready-to-paste prompt (component hooks + current CSS + your image
  // tokens) for ChatGPT/Claude; paste the CSS it returns back into the editor. Images live in their
  // own area: uploads are NOT auto-inserted — each is labelled with the exact url(assets/…) token to
  // copy when you want it. In the Mod Editor sidebar (ModEditor.svelte).
  import { duel } from "../stores/duel.svelte.ts";
  import { themeState, allThemes, setActiveTheme, setBlock, setBlockVar, blockVar, addAsset, activeAssets, removeAsset, assetObjectUrl, displayBlock, editorSel, importModpack, exportModpack, removeModpack } from "../stores/theme.svelte.ts";
  import { THEME_COMPONENTS, componentById } from "../theme/themeComponents.ts";
  import CssEditor from "./ui/CssEditor.svelte";

  const themes = $derived(allThemes());
  const items = [...THEME_COMPONENTS, { id: "custom", label: "Custom CSS", blurb: "" }];
  const current = $derived(items.find((c) => c.id === editorSel.id) ?? items[0]!);

  let modErr = $state("");
  let ta = $state<HTMLTextAreaElement | null>(null);

  const cssText = $derived(displayBlock(editorSel.id));
  // ponytail: live-apply per keystroke — injecting CSS text is cheap, the browser ignores half-typed rules.
  function onCss(v: string): void { setBlock(editorSel.id, v); }

  const isField = $derived(editorSel.id === "duelfield");
  let preview = $state<"classic" | "emz">(duel.format === "free" ? "emz" : "classic");
  function setPreview(m: "classic" | "emz"): void { preview = m; duel.format = m === "emz" ? "free" : "world"; }

  // ── images: thumbnails keyed by name; rebuilt only when the name set changes ──
  const assets = $derived(activeAssets());
  const assetKey = $derived(assets.join(" "));
  let previews = $state<Record<string, string>>({});
  let fileInput = $state<HTMLInputElement | null>(null);
  let dragOver = $state(false);
  $effect(() => {
    assetKey; // dependency: re-run only when the set of names changes
    const names = activeAssets();
    let made: string[] = [];
    let cancelled = false;
    (async () => {
      const map: Record<string, string> = {};
      for (const n of names) { const u = await assetObjectUrl(n); if (u) { map[n] = u; made.push(u); } }
      if (cancelled) made.forEach((u) => URL.revokeObjectURL(u));
      else previews = map;
    })();
    return () => { cancelled = true; made.forEach((u) => URL.revokeObjectURL(u)); };
  });

  async function addFiles(files: File[]): Promise<void> {
    for (const f of files) if (f.type.startsWith("image/")) await addAsset(f); // stored, NOT inserted into CSS
  }
  function onPickFiles(e: Event): void {
    const input = e.currentTarget as HTMLInputElement;
    const files = [...(input.files ?? [])];
    input.value = "";
    void addFiles(files);
  }
  function onDropFiles(e: DragEvent): void {
    e.preventDefault();
    dragOver = false;
    void addFiles([...(e.dataTransfer?.files ?? [])]);
  }

  // ── copy helpers ──
  let copied = $state<string | null>(null); // which token/button last copied (for feedback)
  let copyTimer: ReturnType<typeof setTimeout> | undefined;
  async function copy(text: string, tag: string): Promise<void> {
    try { await navigator.clipboard.writeText(text); copied = tag; clearTimeout(copyTimer); copyTimer = setTimeout(() => (copied = null), 1400); }
    catch { /* clipboard blocked — no-op */ }
  }
  const tokenOf = (name: string): string => `url(assets/${name})`;

  /** A cleaned outerHTML of the component's rendered root — so the AI sees the REAL markup and can
   *  write bespoke selectors. svg innards dropped; src/href/d-style noise attrs removed; class/id/
   *  role/aria/data kept (the useful hooks); whitespace collapsed; capped. `.tp-app` (the whole app)
   *  is excluded as a root so "global" doesn't dump the entire page. */
  function domSnapshot(): string {
    if (typeof document === "undefined") return "";
    const c = componentById(editorSel.id);
    const sels = (c?.sections.flatMap((s) => s.selectors ?? []) ?? []).map((s) => s.sel).filter((s) => s !== ".tp-app");
    const els = sels.map((s) => document.querySelector(s)).filter(Boolean) as Element[];
    if (!els.length) return "";
    // root = the matched element that contains the most of the others (the component's container)
    let root = els[0]!, best = -1;
    for (const e of els) { const n = els.filter((o) => e === o || e.contains(o)).length; if (n > best) { best = n; root = e; } }

    const clone = root.cloneNode(true) as Element;
    clone.querySelectorAll("svg").forEach((s) => (s.innerHTML = "")); // drop icon path data
    [clone, ...clone.querySelectorAll("*")].forEach((el) => { // include the root itself, not just descendants
      for (const at of [...el.attributes]) {
        const n = at.name;
        if (n === "style" || n === "id" || n === "role" || n.startsWith("aria-") || n.startsWith("data-")) continue; // keep in full
        if (n === "class") { // keep, minus Svelte's per-build scope hashes (s-xxxxxx) — pure noise
          const cls = at.value.split(/\s+/).filter((c) => c && !/^s-[\w-]{6,}$/.test(c)).join(" ");
          if (cls) el.setAttribute(n, cls); else el.removeAttribute(n);
          continue;
        }
        el.removeAttribute(n); // src/href/d/alt/title/… — noise for theming
      }
    });
    return clone.outerHTML.replace(/>\s+</g, "><").trim(); // full markup, no cap
  }

  /** A ready-to-paste prompt for an external AI: hooks + the live DOM + current CSS + image tokens. */
  function aiPrompt(): string {
    const c = componentById(editorSel.id);
    const label = c?.label ?? "Custom CSS";
    const sels = c?.sections.flatMap((s) => s.selectors ?? []) ?? [];
    const vars = c?.sections.flatMap((s) => s.vars ?? []) ?? [];
    const out: string[] = [];
    out.push(`I'm theming the "${label}" part of YGO Evolution (a browser Yu-Gi-Oh game) with a "Texture Pack" — plain CSS injected over the app's UI. Write CSS for the look I describe at the end.`);
    out.push(`Rules:\n- Output only CSS — no markdown, no explanation.\n- The stable classes/variables below are the safe, supported hooks — prefer them. You're ALSO free to write bespoke selectors against any real class/element shown in the live DOM below when you need finer control; keep them scoped to this component.\n- Our CSS is injected unlayered so it beats the app's utility classes — but inline style="" attributes you see will still win, so don't rely on overriding those.\n- Self-contained: no @import and no external url(). Reference my images with the exact url(assets/…) tokens shown.`);
    if (sels.length) out.push(`Stable classes:\n${sels.map((s) => `  ${s.sel} — ${s.explain}`).join("\n")}`);
    if (vars.length) out.push(`Variables I can set (default shown):\n${vars.map((v) => `  ${v.key}: ${v.def};  /* ${v.explain} */`).join("\n")}`);
    if (!sels.length && !vars.length) out.push(`This is free-form custom CSS — target any selector you like.`);
    const dom = domSnapshot();
    if (dom) out.push(`Live DOM — this component's current markup (real classes you can target; svg icons + image/path attrs trimmed):\n${dom}`);
    const imgs = activeAssets();
    if (imgs.length) out.push(`My uploaded images — reference each with the EXACT token shown:\n${imgs.map((n) => `  ${tokenOf(n)}   — image file "${n}"`).join("\n")}`);
    else out.push(`I have no uploaded images yet. If the look needs one, tell me what to upload.`);
    out.push(`Current CSS:\n${displayBlock(editorSel.id)}`);
    return out.join("\n\n");
  }

  // ── Field helper: playmat image / main duel background ──
  async function onPlaymat(e: Event): Promise<void> {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    const name = await addAsset(file);
    setBlockVar("duelfield", "--df-field", `center / contain no-repeat url(assets/${name})`);
    setBlockVar("duelfield", "--df-zone-dashed", "1");
  }
  function onMainBg(e: Event): void { setBlockVar("duelfield", "--df-bg-field", (e.currentTarget as HTMLInputElement).value); }
  async function onBgImage(e: Event): Promise<void> {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    const name = await addAsset(file);
    setBlockVar("duelfield", "--df-bg-field", `center / cover no-repeat url(assets/${name})`);
  }
  const mainBgColor = $derived(/^#[0-9a-fA-F]{6}$/.test(blockVar("duelfield", "--df-bg-field")) ? blockVar("duelfield", "--df-bg-field") : "#0a1020");

  async function onImport(e: Event): Promise<void> {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    modErr = "";
    if (!file) return;
    try { await importModpack(file); }
    catch (err) { modErr = (err as Error).message || "Couldn't import that pack."; }
  }
</script>

{#snippet check()}
  <svg viewBox="0 0 24 24" class="size-3.5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
{/snippet}

<div class="flex flex-col gap-2.5 p-3">
  <!-- pack switcher -->
  <div class="flex flex-wrap gap-1">
    {#each themes as t (t.id)}
      <span class="flex items-center">
        <button
          onclick={() => setActiveTheme(t.id)}
          class="rounded px-2.5 py-1 text-xs font-semibold transition-colors {t.id === themeState.activeId ? 'bg-cyan/15 text-cyan' : 'bg-void/40 text-ink-dim hover:bg-surface-2/50'}"
        >{t.name}</button>
        {#if !t.builtin}
          <button onclick={() => void removeModpack(t.id)} class="px-1 text-ink-dim transition-colors hover:text-magenta" aria-label="Delete {t.name}">
            <svg viewBox="0 0 24 24" class="size-3" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
        {/if}
      </span>
    {/each}
  </div>

  <!-- what you're theming -->
  <div class="relative">
    <select
      value={editorSel.id}
      onchange={(e) => (editorSel.id = (e.currentTarget as HTMLSelectElement).value)}
      class="w-full appearance-none rounded bg-void/50 py-2 pl-2.5 pr-8 font-display text-[0.78rem] font-semibold text-ink ring-1 ring-cyan-dim/30 transition-colors focus:outline-none focus:ring-cyan/60"
      aria-label="Component to theme"
    >
      {#each items as c (c.id)}<option value={c.id} class="bg-void font-body text-ink">{c.label}</option>{/each}
    </select>
    <svg viewBox="0 0 24 24" class="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-cyan-dim" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6" /></svg>
  </div>

  <!-- export a prompt for your own AI (ChatGPT / Claude) -->
  <button
    onclick={() => copy(aiPrompt(), "prompt")}
    class="flex w-full items-center justify-center gap-1.5 chamfer-btn bg-cyan/20 px-3 py-2 font-display text-[0.64rem] font-semibold uppercase tracking-[0.14em] text-cyan transition-colors hover:bg-cyan/30"
    style="--c:6px"
  >
    {#if copied === "prompt"}
      <span class="text-lime">{@render check()}</span>
    {:else}
      <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
    {/if}
    Copy prompt for AI
  </button>

  <!-- ── images: upload area + token list (uploads are NOT auto-inserted) ── -->
  <div
    role="button"
    tabindex="0"
    aria-label="Add images"
    class="flex cursor-pointer flex-col items-center gap-1 rounded-md border border-dashed px-3 py-3 text-center transition-colors {dragOver ? 'border-cyan bg-cyan/10' : 'border-cyan-dim/40 hover:border-cyan/60 hover:bg-void/40'}"
    ondragover={(e) => { e.preventDefault(); dragOver = true; }}
    ondragleave={() => (dragOver = false)}
    ondrop={onDropFiles}
    onclick={() => fileInput?.click()}
    onkeydown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileInput?.click(); } }}
  >
    <svg viewBox="0 0 24 24" class="size-5 text-cyan-dim" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4m0 0L8 8m4-4 4 4" /><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></svg>
    <span class="text-[0.68rem] font-medium text-ink-dim">Drop images or <span class="text-cyan">browse</span></span>
    <input bind:this={fileInput} type="file" accept="image/*" multiple class="hidden" onchange={onPickFiles} />
  </div>

  {#if assets.length}
    <ul class="flex flex-col gap-1.5">
      {#each assets as name (name)}
        <li class="flex items-center gap-2 rounded bg-void/40 p-1.5">
          <span class="size-9 shrink-0 overflow-hidden rounded bg-void ring-1 ring-cyan-dim/25">
            {#if previews[name]}<img src={previews[name]} alt={name} class="size-full object-cover" />{/if}
          </span>
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <span class="truncate text-[0.68rem] font-medium text-ink">{name}</span>
            <button
              onclick={() => copy(tokenOf(name), name)}
              class="flex items-center gap-1 self-start rounded bg-void/60 px-1.5 py-0.5 font-mono text-[0.62rem] text-cyan-dim transition-colors hover:text-cyan"
              title="Copy this CSS reference"
            >
              {#if copied === name}{@render check()}<span class="text-lime">copied</span>{:else}url(assets/{name})<svg viewBox="0 0 24 24" class="size-3 opacity-70" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>{/if}
            </button>
          </span>
          <button onclick={() => void removeAsset(name)} class="shrink-0 self-start px-1 text-ink-dim transition-colors hover:text-magenta" aria-label="Delete image {name}">
            <svg viewBox="0 0 24 24" class="size-3.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
        </li>
      {/each}
    </ul>
  {/if}

  {#if isField}
    <!-- duel field: drag the zones right on the board + quick image/background -->
    <div class="mt-1 h-px bg-cyan-dim/15"></div>
    <button
      onclick={() => (editorSel.fieldHelper = !editorSel.fieldHelper)}
      class="flex items-center justify-between gap-2 rounded bg-void/40 px-2.5 py-1.5 text-left transition-colors hover:bg-void/60"
      role="switch"
      aria-checked={editorSel.fieldHelper}
    >
      <span class="flex items-center gap-1.5 font-display text-[0.64rem] font-semibold text-ink">
        <svg viewBox="0 0 24 24" class="size-3.5 text-cyan-dim" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9 2 12l3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20" /></svg>
        Drag zones on board
      </span>
      <span class="relative h-5 w-9 shrink-0 rounded-full transition-colors duration-200 {editorSel.fieldHelper ? 'bg-cyan/70 glow-cyan' : 'bg-void/70 ring-1 ring-ink-dim/30'}">
        <span class="absolute top-0.5 size-4 rounded-full transition-all duration-200 {editorSel.fieldHelper ? 'left-[1.125rem] bg-void' : 'left-0.5 bg-ink'}"></span>
      </span>
    </button>
    <div class="grid grid-cols-[1fr_1fr_auto] gap-1.5">
      <label class="flex cursor-pointer items-center justify-center gap-1.5 chamfer-btn bg-void/50 px-2 py-1.5 text-[0.58rem] font-semibold text-ink-dim transition-colors hover:text-cyan" style="--c:5px" title="Set the playmat behind the zones">
        <svg viewBox="0 0 24 24" class="size-3.5" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></svg>
        Playmat
        <input type="file" accept="image/*" class="hidden" onchange={onPlaymat} />
      </label>
      <label class="flex cursor-pointer items-center justify-center gap-1.5 chamfer-btn bg-void/50 px-2 py-1.5 text-[0.58rem] font-semibold text-ink-dim transition-colors hover:text-cyan" style="--c:5px" title="Set a background image behind the duel">
        <svg viewBox="0 0 24 24" class="size-3.5" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></svg>
        Background
        <input type="file" accept="image/*" class="hidden" onchange={onBgImage} />
      </label>
      <label class="relative flex cursor-pointer items-center chamfer-btn bg-void/50 px-2.5 py-1.5 transition-colors hover:bg-void/70" style="--c:5px" title="Background colour">
        <span class="size-3.5 shrink-0 rounded-sm ring-1 ring-ink-dim/40" style="background:{mainBgColor}"></span>
        <input type="color" value={mainBgColor} class="absolute size-0 opacity-0" oninput={onMainBg} />
      </label>
    </div>
    <div class="flex items-center gap-0.5 rounded bg-void/50 p-0.5">
      <button onclick={() => setPreview("classic")} class="flex-1 rounded px-2 py-1 font-display text-[0.56rem] font-semibold uppercase tracking-wide transition-colors {preview === 'classic' ? 'bg-cyan/20 text-cyan' : 'text-ink-dim hover:text-cyan'}">Classic</button>
      <button onclick={() => setPreview("emz")} class="flex-1 rounded px-2 py-1 font-display text-[0.56rem] font-semibold uppercase tracking-wide transition-colors {preview === 'emz' ? 'bg-cyan/20 text-cyan' : 'text-ink-dim hover:text-cyan'}">Link / EMZ</button>
    </div>
  {/if}

  <!-- raw CSS: paste AI output here or hand-edit; capped + scrollable so it never dominates the panel -->
  <div>
    <div class="mb-1 font-display text-[0.56rem] font-semibold uppercase tracking-[0.18em] text-cyan-dim/80">CSS</div>
    <div class="max-h-[17rem] overflow-y-auto overflow-x-hidden rounded scroll-cyber">
      <CssEditor
        bind:textarea={ta}
        value={cssText}
        oninput={onCss}
        placeholder="/* Paste AI-written CSS here, or edit by hand. */"
        class="min-h-[9rem]"
      />
    </div>
  </div>

  <!-- share -->
  <div class="mt-1 flex items-center gap-2 border-t border-cyan-dim/15 pt-3">
    <label class="cursor-pointer chamfer bg-cyan/15 px-3 py-1.5 font-display text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-cyan transition-colors hover:bg-cyan/25" style="--c:6px">
      Import
      <input type="file" accept=".zip,application/zip" class="hidden" onchange={onImport} />
    </label>
    <button onclick={() => void exportModpack(themeState.activeId)} class="chamfer bg-void/50 px-3 py-1.5 font-display text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-ink-dim transition-colors hover:text-cyan" style="--c:6px">
      Export
    </button>
    {#if modErr}<span class="text-[0.66rem] text-magenta">{modErr}</span>{/if}
  </div>
</div>
